import React, { forwardRef, useState, DragEvent } from 'react'
import {
  useDrag,
  useDrop,
  UseDropProps,
  ShareDropPayload,
  ProjectDropPayload,
} from '../../utils'
import {
  SelectDropdownLinkItem,
  SelectDropdownLinkItemProps,
} from '@sketch/components'

import { useFlag } from '../../flags'

interface DroppableLinkItemProps
  extends SelectDropdownLinkItemProps,
    UseDropProps {
  forceHighlight?: boolean
  isDroppableAreaEnabled?: boolean
  projectIdentifier?: string
}

const useEnableDroppableArea = ({
  isDroppableAreaEnabled,
  onShareDragOver,
  onProjectDragOver,
  onUnknownDragOver,
}: Pick<
  DroppableLinkItemProps,
  | 'isDroppableAreaEnabled'
  | 'onShareDragOver'
  | 'onProjectDragOver'
  | 'onUnknownDragOver'
>) => {
  const [enableDroppableArea, setEnableDroppableArea] = useState(false)

  const onShareDragOverHandler = (data: ShareDropPayload) => {
    if (onShareDragOver) {
      onShareDragOver(data)
    } else {
      setEnableDroppableArea(true)
    }
  }

  const onProjectDragOverHandler = (data: ProjectDropPayload) => {
    if (onProjectDragOver) {
      onProjectDragOver(data)
    } else {
      setEnableDroppableArea(true)
    }
  }

  const onUnknownDragOverHandler = (event: DragEvent<HTMLElement>) => {
    if (onUnknownDragOver) {
      onUnknownDragOver(event)
    } else {
      setEnableDroppableArea(false)
    }
  }

  return {
    enableDroppableArea: isDroppableAreaEnabled || enableDroppableArea,
    onShareDragOverHandler,
    onProjectDragOverHandler,
    onUnknownDragOverHandler,
  }
}

// TODO: Review react/display-name ESLint issues
//  see: https://github.com/sketch-hq/Cloud/issues/14818
// eslint-disable-next-line react/display-name
export const DroppableLinkItem = forwardRef(
  (props: DroppableLinkItemProps, ref) => {
    const {
      forceHighlight = false,
      onShareDropped,
      onProjectDropped,
      onShareDragOver,
      onProjectDragOver,
      onUnknownDragOver,
      isDroppableAreaEnabled,
      projectIdentifier,
      draggable = false,
      ...linkProps
    } = props

    const isNestedProjectsOn = useFlag('nested-projects')

    /**
     * For folders in the sidebar we want to manage these handlers in
     * ProjectListItem and pass them as props, because there we have all the
     * info needed to decide if a droppable area should be enabled or not.
     *
     * But for other situations, like Trash, we just want to enable the
     * droppable area if the user is dragging something *we know* over it
     *  (a share or a folder).
     */
    const {
      onShareDragOverHandler,
      onProjectDragOverHandler,
      onUnknownDragOverHandler,
      enableDroppableArea,
    } = useEnableDroppableArea({
      isDroppableAreaEnabled,
      onShareDragOver,
      onProjectDragOver,
      onUnknownDragOver,
    })

    const dragEvents = useDrag({
      type: 'project',
      identifier: projectIdentifier ?? '',
      customDragImage: true,
      customDragImageStyles: {
        transform: 'scale(0.8)',
      },
    })

    const { isDraggedOver, kind, isDraggedOverItself, ...dropEvents } = useDrop(
      {
        onShareDropped,
        onProjectDropped,
        onShareDragOver: onShareDragOverHandler,
        onProjectDragOver: onProjectDragOverHandler,
        onUnknownDragOver: onUnknownDragOverHandler,
      }
    )

    const shouldForceHover = isNestedProjectsOn
      ? enableDroppableArea && isDraggedOver && !isDraggedOverItself
      : isDraggedOver && !isDraggedOverItself

    return (
      <SelectDropdownLinkItem
        forceHover={shouldForceHover}
        forceActive={forceHighlight}
        ref={ref}
        {...linkProps}
        draggable={draggable}
        // `draggable` already implies that `projectIdentifier` is defined
        // (see ProjectListItem), but just to be safe we double check here
        {...(draggable && projectIdentifier ? dragEvents : {})}
        {...dropEvents}
      />
    )
  }
)
